import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import {
  AnonymousConsent,
  AnonymousConsentsConfig,
  AnonymousConsentsService,
  AuthConfigService,
  ConsentTemplate,
  Country,
  EventService,
  GlobalMessageService,
  GlobalMessageType,
  OAuthFlow,
  Region,
  RoutingService,
  UserAddressService,
  WindowRef
} from '@spartacus/core';
import { UserRegisterFacade } from '@spartacus/user/profile/root';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ICON_TYPE } from '../../../../../custom/icons/icon.model';
import { CustomUserSignUp } from '../../../../custom/model/user-profile.model';
import { CustomValidators } from '../../../../custom/shared/utils/validators/custom-validators';
import { JobType, UserTaxGroup } from '../../../../spartacus-custom.module';
import { RecaptchaService } from '../../../recaptcha/recaptcha.service';
import { CustomUserActions } from '../../../tracking/custom-events/user/store/actions';
import { CustomUserRegisterFacade } from '../root/facade/custom-user-register.facade';
@Component({
  selector: 'cx-register',
  templateUrl: './custom-register.component.html',
  styleUrls: ['./custom-register.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CustomRegisterComponent implements OnInit, OnDestroy, AfterViewInit {
  jobTypes$?: Observable<JobType[]>;
  regions$?: Observable<Region[]>;
  userTaxGroups$?: Observable<UserTaxGroup[]>;
  showProfessionalForm = false;
  iconTypes = ICON_TYPE;
  showPassword = false;
  isLoading$ = new BehaviorSubject(false);
  validateTaxIdentification = false;
  isClicked = false;
  protected subscriptions: Subscription = new Subscription();
  anonymousConsent$?: Observable<{
    consent: AnonymousConsent | undefined;
    template: string | undefined;
  }>;
  fb: UntypedFormBuilder = new UntypedFormBuilder();
  registerForm: UntypedFormGroup = this.fb.group({
    firstName: ['', [Validators.required, Validators.maxLength(70)]],
    contactPerson: [''],
    documentIdentifier: [''],
    jobType: [''],
    mobilePhone: [''],
    countryCode: [''],
    line1: [''],
    town: [''],
    postalCode: [''],
    regionCode: [''],
    userTaxGroup: [''],
    professional: [false],
    email: ['', [Validators.required, CustomValidators.emailValidator]],
    password: ['', CustomValidators.passwordValidators],
    termsAndConditions: [false, !this.showProfessionalForm ? [Validators.requiredTrue] : ''],
    newsletter: [false],
    exclusiveRole: [!this.showProfessionalForm ? 'ohgarcustomergroup' : 'ohgarprofessionalgroup']
  });
  changeValidators = false;

  constructor(
    protected userRegister: UserRegisterFacade,
    protected customUserRegister: CustomUserRegisterFacade,
    protected userAddressService: UserAddressService,
    protected globalMessageService: GlobalMessageService,
    protected router: RoutingService,
    protected anonymousConsentsService: AnonymousConsentsService,
    protected anonymousConsentsConfig: AnonymousConsentsConfig,
    protected authConfigService: AuthConfigService,
    protected cd: ChangeDetectorRef,
    protected recaptchaService: RecaptchaService,
    protected winRef: WindowRef,
    protected eventService: EventService,
    private store: Store
  ) {}

  updateValidators(): void {
    if (this.validateTaxIdentification) {
      this.removeValidators(this.registerForm);
      this.registerForm.get('mobilePhone')!.setValidators([Validators.required, CustomValidators.phoneValidator]);
      this.registerForm.get('line1')!.setValidators([Validators.required, Validators.maxLength(70)]);
      this.registerForm.get('town')!.setValidators([Validators.required, Validators.maxLength(35)]);
      this.registerForm.get('postalCode')!.setValidators([Validators.required, Validators.maxLength(5), Validators.minLength(5), CustomValidators.onlyNumbers]);
      this.registerForm.get('regionCode')!.setValidators([Validators.required, CustomValidators.regionCodeRegisterStartsWith('postalCode')]);
      this.registerForm.get('userTaxGroup')!.setValidators(Validators.required);
      this.registerForm.get('termsAndConditions')!.setValidators(Validators.requiredTrue);
      this.registerForm.get('countryCode')!.setValue('ES');
    } else {
      this.registerForm.get('exclusiveRole')!.setValue('ohgarprofessionalgroup');
      this.registerForm.get('contactPerson')!.setValidators([Validators.required, Validators.maxLength(35)]);
      this.registerForm.get('documentIdentifier')!.setValidators([Validators.required, CustomValidators.documentIdentifier('ohgarprofessionalgroup', 'ES')]);
      this.registerForm.get('jobType')!.setValidators(Validators.required);
      this.registerForm.get('termsAndConditions')!.clearValidators();
      this.registerForm.get('termsAndConditions')!.updateValueAndValidity();
      this.registerForm.get('newsletter')!.setValue(false);
    }
  }

  public removeValidators(form: UntypedFormGroup): void {
    for (const key in form.controls) {
      form.get(key)!.clearValidators();
      form.get(key)!.updateValueAndValidity();
    }
  }

  isPasswordHidden(): void {
    this.showPassword = !this.showPassword;
  }

  isValidateTaxIdentification(): void {
    if (this.registerForm.valid) {
      this.documentIdentifierValidator();
    } else {
      this.registerForm.markAllAsTouched();
    }
  }

  updateFirstStepValidators(): void {
    this.registerForm.get('contactPerson')!.setValidators([Validators.required, Validators.maxLength(35)]);
    this.registerForm.get('firstName')!.setValidators([Validators.required, Validators.maxLength(70)]);
    this.registerForm.get('email')!.setValidators([Validators.required, CustomValidators.emailValidator]);
    this.registerForm.get('password')!.setValidators(CustomValidators.passwordValidators);
    this.registerForm.get('documentIdentifier')!.setValidators([Validators.required, CustomValidators.documentIdentifier('ohgarprofessionalgroup', 'ES')]);
    this.registerForm.get('jobType')!.setValidators(Validators.required);
  }

  goBackToSecondStep(): void {
    this.validateTaxIdentification = false;
    this.changeValidators = false;
    this.removeValidators(this.registerForm);
    this.updateFirstStepValidators();
    this.registerForm.markAsUntouched();
  }

  updateForm(): void {
    this.updateValidators();
    this.registerForm.updateValueAndValidity();
  }

  professional(value: boolean): void {
    const body = this.winRef.document.body;
    this.registerForm.reset();
    this.showProfessionalForm = value;
    this.registerForm.get('professional')?.setValue(value);

    if (value) {
      body.classList.add('professional');
    } else {
      body.classList.remove('professional');
    }
  }

  ngOnInit(): void {
    this.regions$ = this.userAddressService.getRegions('ES').pipe(
      map((countries: Country[]) => {
        return countries;
      })
    );

    this.jobTypes$ = this.customUserRegister.getJobTypes().pipe(
      tap((jobTypes: JobType[]) => {
        if (Object.keys(jobTypes).length === 0) {
          this.customUserRegister.loadJobTypes();
        }
      })
    );

    this.userTaxGroups$ = this.customUserRegister.getUserTaxGroups().pipe(
      tap((userTaxGroups: UserTaxGroup[]) => {
        if (Object.keys(userTaxGroups).length === 0) {
          this.customUserRegister.loadUserTaxGroups();
        }
      })
    );
    const registerConsent = this.anonymousConsentsConfig?.anonymousConsents?.registerConsent ?? '';

    this.anonymousConsent$ = combineLatest([this.anonymousConsentsService.getConsent(registerConsent), this.anonymousConsentsService.getTemplate(registerConsent)]).pipe(
      map(([consent, template]: [AnonymousConsent?, ConsentTemplate?]) => {
        return {
          consent,
          template: template?.description ? template.description : ''
        };
      })
    );
    this.subscriptions.add(
      // tslint:disable-next-line:no-non-null-assertion
      this.registerForm?.get('newsletter')!.valueChanges?.subscribe(() => {
        this.toggleAnonymousConsent();
      })
    );
  }

  ngAfterViewInit(): void {
    if (this.winRef.localStorage?.isProfessional === 'true') {
      this.professional(true);
      this.updateForm();
      const profRadio = this.winRef.document.getElementById('uno');
    }
  }

  submitForm(): void {
    if (this.registerForm.valid) {
      this.recaptchaService
        .getToken()
        .then((token) => {
          if (token) {
            this.subscriptions.add(
              this.recaptchaService.validateRecaptchaToken(token).subscribe((response) => {
                if (response?.status === 200) {
                  this.registerUser();
                } else {
                  this.recaptchaService.recaptchaError('forms.recaptcha.invalidToken');
                }
              })
            );
          } else {
            this.recaptchaService.recaptchaError('forms.recaptcha.invalidToken');
          }
        })
        .catch(() => this.recaptchaService.recaptchaError(null));
    } else {
      this.userRegisterEventOnFail();
      this.registerForm.markAllAsTouched();
    }
  }

  registerUser(): void {
    this.isLoading$.next(true);
    this.subscriptions.add(
      this.customUserRegister.register(this.collectDataFromRegisterForm(this.registerForm.value)).subscribe({
        next: () => this.onRegisterUserSuccess(),
        complete: () => this.isLoading$.next(false),
        error: () => {
          this.isLoading$.next(false);
        }
      })
    );
  }

  validatePostalCode(): void {
    this.registerForm.get('regionCode')?.updateValueAndValidity();
  }

  changeAetValidators(change: any): void {
    if (change) {
      this.registerForm.get('documentIdentifier')?.setValidators([Validators.required, CustomValidators.documentIdentifier('ohgarprofessionalgroup', 'ES')]);
      this.registerForm.get('firstName')?.setValidators([Validators.required, Validators.maxLength(70)]);
      this.registerForm.get('documentIdentifier')?.updateValueAndValidity();
      this.registerForm.get('firstName')?.updateValueAndValidity();
    }
  }

  documentIdentifierValidator(): void {
    this.isLoading$.next(true);
    this.subscriptions.add(
      this.customUserRegister.documentIdentifierValidator(this.collectDataFromDocumentIdentifierValidatorForm(this.registerForm.value)).subscribe({
        next: () => {
          this.isLoading$.next(false);
          this.validateTaxIdentification = true;
          this.cd.markForCheck();
          this.updateForm();
          this.registerForm.markAsUntouched();
        },
        error: () => {
          this.isLoading$.next(false);
          this.registerForm.get('documentIdentifier')?.setValidators([CustomValidators.aetValidator]);
          this.registerForm.get('firstName')?.setValidators([CustomValidators.aetValidator]);
          this.registerForm.controls.documentIdentifier.markAsTouched();
          this.registerForm.controls.firstName.markAsTouched();
          this.changeValidators = true;
          this.cd.markForCheck();
        }
      })
    );
  }

  collectDataFromRegisterForm(formData: any): CustomUserSignUp {
    const {
      firstName,
      contactPerson,
      email,
      password,
      documentIdentifier,
      jobType,
      line1,
      town,
      mobilePhone,
      postalCode,
      regionCode,
      countryCode,
      userTaxGroup,
      newsletter,
      exclusiveRole,
      termsAndConditions
    } = formData;

    return {
      firstName,
      contactPerson,
      uid: email.toLowerCase(),
      password,
      documentIdentifier,
      jobType,
      line1,
      town,
      mobilePhone,
      postalCode,
      regionCode,
      countryCode,
      userTaxGroup,
      newsletter,
      exclusiveRole,
      termsAndConditions
    };
  }

  collectDataFromDocumentIdentifierValidatorForm(formData: any): CustomUserSignUp {
    const { firstName, documentIdentifier, exclusiveRole } = formData;

    return {
      firstName,
      documentIdentifier,
      exclusiveRole
    };
  }

  isConsentGiven(consent: AnonymousConsent): boolean {
    return this.anonymousConsentsService.isConsentGiven(consent);
  }

  private onRegisterUserSuccess(): void {
    if (this.authConfigService.getOAuthFlow() === OAuthFlow.ResourceOwnerPasswordFlow) {
      this.userRegisterEventOnSuccess();
      this.router.go('login');
    }
    this.globalMessageService.add({ key: 'register.postRegisterMessage' }, GlobalMessageType.MSG_TYPE_CONFIRMATION);
  }

  toggleAnonymousConsent(): void {
    const registerConsent = this.anonymousConsentsConfig?.anonymousConsents?.registerConsent;

    if (registerConsent) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      if (Boolean(this.registerForm.get('newsletter')!.value)) {
        this.anonymousConsentsService.giveConsent(registerConsent);
      } else {
        this.anonymousConsentsService.withdrawConsent(registerConsent);
      }
    }
  }

  ngOnDestroy(): void {
    const body = this.winRef.document.body;
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
    body.classList.remove('professional');
  }

  private userRegisterEventOnSuccess() {
    this.store.dispatch(
      new CustomUserActions.UserRegisteredSuccess({
        payload: this.registerForm.value
      })
    );
  }

  private userRegisterEventOnFail() {
    this.store.dispatch(
      new CustomUserActions.UserRegisteredFail({
        payload: this.registerForm.value
      })
    );
  }
}
